import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { getProductImage } from '@/lib/productMedia';
import { useSectionContent } from '@/hooks/useSectionContent';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';
import { trackEvent, trackAddToCart } from '@/lib/facebookPixel';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/hooks/useAuth';
import { useFavorites } from '@/hooks/useFavorites';
import FavoriteBadge from '@/components/feature/FavoriteBadge';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { normalizeCurrency } from '@/lib/currency';
import { isDigitalProduct } from '@/lib/productType';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { fetchCatalogProductsCached } from '@/lib/coreCatalog';

interface ProductItem {
  id: number;
  name: string;
  description: string | null;
  price: number;
  discount_enabled: boolean;
  discount_price: number | null;
  media: unknown;
  category_id: number | null;
  status: string;
  created_at: string;
  stock: number | null;
  currency: string | null;
  product_type: string | null;
  digital_file_url: string | null;
}

// Un produit est marqué « Nouveau » pendant cette fenêtre après sa création.
const NEW_PRODUCT_WINDOW_DAYS = 14;

function isNewProduct(createdAt: string): boolean {
  if (!createdAt) return false;
  const diff = Date.now() - new Date(createdAt).getTime();
  return diff >= 0 && diff <= NEW_PRODUCT_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

export default function ProduitsPublic() {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [categories, setCategories] = useState<{ id: number; nom: string; icon: string | null; image_couverture: string | null; parent_id: number | null; sort_order?: number }[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [activeSubCategory, setActiveSubCategory] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { tenant, isTenant } = useTenant();
  const { before, highlight, subtitle } = useSectionContent('produits');
  // Suivi de visite de la page produits publique (vues, IP, géo, provenance).
  useThemePageTracking('produits', tenant?.id ?? null, isTenant && !!tenant);
  const { addToCart, currency } = useCart();
  const { isFav, toggle: toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Bascule un favori depuis la liste publique (badge « déjà en favori »).
  const toggleFavItem = (id: number, label: string, metadata: Record<string, unknown>) => {
    if (!user) { navigate('/login-client'); return; }
    void toggleFavorite({ userId: user.id, entityType: 'product', entityId: id, label, metadata });
  };

  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    const run = async () => {
      try {
        // Vitrine d'un marchand : la liste est servie depuis le cache
        // tenant-aware (invalidé automatiquement à chaque écriture produit).
        if (isTenant && tenant?.id) {
          try {
            const org = await getOrganizationByCommerce(tenant.id);
            if (org) {
              const cachedList = (await fetchCatalogProductsCached({
                organizationId: org.id,
                tenantId: org.tenant_id,
                commerceId: tenant.id,
              })) as unknown as ProductItem[];
              if (!cancelled) {
                setProducts(cachedList);
                if (cachedList.length > 0) {
                  trackEvent('ViewContent', {
                    content_type: 'product',
                    content_ids: cachedList.map((p) => String(p.id)),
                  });
                }
              }
              return;
            }
          } catch {
            /* socle indisponible : repli sur la lecture directe */
          }
        }

        // Repli (marketplace ou socle non initialisé) : lecture directe.
        let query = supabase.from('product_items').select('*').eq('status', 'active');
        if (isTenant && tenant) {
          query = query.eq('idcommerce', tenant.id);
        }
        const { data, error: fetchError } = await query.order('created_at', { ascending: false });
        if (cancelled) return;
        if (fetchError) setError(fetchError.message);
        else {
          const list = (data as ProductItem[]) || [];
          setProducts(list);
          // Suivi Facebook Pixel : consultation de la page produits.
          if (list.length > 0) {
            trackEvent('ViewContent', {
              content_type: 'product',
              content_ids: list.map((p) => String(p.id)),
            });
          }
        }
      } catch {
        if (!cancelled) setError('Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isTenant, tenant]);

  // Catégories créées par le marchand (avec filtre par catégorie sur la boutique).
  useEffect(() => {
    if (!isTenant || !tenant) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    supabase
      .from('categorie')
      .select('id, nom, icon, image_couverture, parent_id, sort_order')
      .eq('idcommerce', tenant.id)
      .order('sort_order', { ascending: true })
      .order('nom', { ascending: true })
      .then(({ data }) => {
        if (!cancelled) setCategories((data as { id: number; nom: string; icon: string | null; image_couverture: string | null; parent_id: number | null; sort_order?: number }[]) || []);
      })
      .catch(() => { if (!cancelled) setCategories([]); });
    return () => { cancelled = true; };
  }, [isTenant, tenant]);

  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const { display, original } = useDisplayCurrency(monaie);

  // Catégories racines (sans parent) et sous-catégories de la catégorie active.
  const rootCategories = categories.filter((c) => !c.parent_id);
  const subCategories = activeCategory ? categories.filter((c) => c.parent_id === activeCategory) : [];

  // Produits visibles selon la catégorie / sous-catégorie sélectionnée.
  const visibleProducts = (() => {
    let list = products;
    if (activeSubCategory) list = list.filter((p) => p.category_id === activeSubCategory);
    else if (activeCategory) {
      const childIds = categories.filter((c) => c.parent_id === activeCategory).map((c) => c.id);
      const ids = new Set<number>([activeCategory, ...childIds]);
      list = list.filter((p) => p.category_id != null && ids.has(p.category_id));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [p.name, p.description].filter(Boolean).join(' ').toLowerCase().includes(q),
      );
    }
    return list;
  })();

  const handleAdd = (product: ProductItem) => {
    const price = Number(product.price || 0);
    const promo = product.discount_enabled && product.discount_price != null;
    const effective = promo ? Number(product.discount_price) : price;
    const digital = isDigitalProduct(product);
    const stock = digital ? 9999 : (product.stock ?? 9999);
    addToCart({
      id: product.id,
      name: product.name,
      price: effective,
      original_price: promo ? price : null,
      media: getProductImage(product.media) || '',
      stock,
      product_type: product.product_type || 'physical',
      digital_file_url: product.digital_file_url,
    });
    trackAddToCart({
      content_ids: [String(product.id)],
      content_name: product.name,
      value: effective,
      currency: currency || monaie,
      num_items: 1,
    });
  };

  // Nombre de produits par catégorie (une catégorie racine compte ses sous-catégories).
  const countForCategory = (catId: number): number => {
    const childIds = categories.filter((c) => c.parent_id === catId).map((c) => c.id);
    const ids = new Set<number>([catId, ...childIds]);
    return products.filter((p) => p.category_id != null && ids.has(p.category_id)).length;
  };
  const countForSubCategory = (catId: number): number =>
    products.filter((p) => p.category_id === catId).length;

  // Chaîne de catégorie complète d'un contenu (Catégorie › Sous-catégorie › Sous-sous-catégorie).
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const categoryChainFor = (id: number | null): { id: number; nom: string }[] => {
    const out: { id: number; nom: string }[] = [];
    let cur: number | null = id;
    let guard = 0;
    while (cur && guard < 6) {
      guard += 1;
      const c = categoryById.get(cur);
      if (!c) break;
      out.unshift({ id: c.id, nom: c.nom });
      cur = c.parent_id ?? null;
    }
    return out;
  };

  const renderCategoryIcon = (icon: string | null, image: string | null, active: boolean) => {
    if (icon) return <i className={`${icon} text-sm`}></i>;
    if (image) {
      return (
        <span className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border ${active ? 'border-white/40' : 'border-background-200/70'}`}>
          <img src={image} alt="" className="w-full h-full object-cover" />
        </span>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background-100/50 to-transparent"></div>
        <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-4">
            {before}
            {before && highlight ? ' ' : ''}
            {highlight && <span className="text-primary-500">{highlight}</span>}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 max-w-xl mx-auto">
            {subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 md:py-12">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          {!loading && !error && products.length > 0 && (
            <div className="mb-5">
              <div className="relative w-full sm:max-w-md">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un produit…"
                  className="w-full pl-10 pr-10 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                    aria-label="Effacer la recherche"
                  >
                    <i className="ri-close-circle-line"></i>
                  </button>
                )}
              </div>
            </div>
          )}
          {!loading && !error && categories.length > 0 && (
            <div className="mb-6 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setActiveCategory(null); setActiveSubCategory(null); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    activeCategory === null
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-apps-2-line"></i>
                  Tous
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeCategory === null ? 'bg-white/25 text-background-50' : 'bg-background-200/70 text-foreground-600'}`}>{products.length}</span>
                </button>
                {rootCategories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => { setActiveCategory(cat.id); setActiveSubCategory(null); }}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {renderCategoryIcon(cat.icon, cat.image_couverture, activeCategory === cat.id)}
                    {cat.nom}
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeCategory === cat.id ? 'bg-white/25 text-background-50' : 'bg-background-200/70 text-foreground-600'}`}>{countForCategory(cat.id)}</span>
                  </button>
                ))}
              </div>
              {subCategories.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap pl-1">
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-corner-down-right-line"></i>
                    Sous-catégories :
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveSubCategory(null)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      activeSubCategory === null
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
                    }`}
                  >
                    Tout afficher
                  </button>
                  {subCategories.map((sub) => (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => setActiveSubCategory(sub.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                        activeSubCategory === sub.id
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
                      }`}
                    >
                      {renderCategoryIcon(sub.icon, sub.image_couverture, activeSubCategory === sub.id)}
                      {sub.nom}
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${activeSubCategory === sub.id ? 'bg-white/25 text-background-50' : 'bg-white/70 text-accent-800'}`}>{countForSubCategory(sub.id)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {!loading && !error && (search.trim() || activeCategory !== null || activeSubCategory !== null) && (
            <div className="mb-5 flex items-center gap-2 flex-wrap px-3.5 py-2.5 rounded-lg bg-primary-50 border border-primary-200/60">
              <i className="ri-filter-3-line text-primary-600"></i>
              <span className="text-sm font-semibold text-foreground-800">
                {visibleProducts.length} résultat{visibleProducts.length > 1 ? 's' : ''}
              </span>
              {search.trim() && <span className="text-xs text-foreground-500">pour « {search.trim()} »</span>}
              {activeSubCategory !== null && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 font-medium">
                  {categories.find((c) => c.id === activeSubCategory)?.nom}
                </span>
              )}
              {activeSubCategory === null && activeCategory !== null && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-medium">
                  {categories.find((c) => c.id === activeCategory)?.nom}
                </span>
              )}
              <button
                type="button"
                onClick={() => { setSearch(''); setActiveCategory(null); setActiveSubCategory(null); }}
                className="ml-auto text-xs font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-0.5"></i>Réinitialiser
              </button>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20">
              <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
              <p className="text-foreground-500">{error}</p>
            </div>
          ) : visibleProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-shopping-bag-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">{search.trim() ? 'Aucun produit ne correspond à votre recherche' : 'Aucun produit pour le moment'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {visibleProducts.map((product) => {
                const imgSrc = getProductImage(product.media);
                const price = Number(product.price || 0);
                const promo = product.discount_enabled && product.discount_price != null;
                const eff = promo && product.discount_price != null ? Number(product.discount_price) : price;
                const digital = isDigitalProduct(product);
                const outOfStock = !digital && product.stock != null && product.stock <= 0;
                const cur = normalizeCurrency(product.currency || monaie);
                const chain = categoryChainFor(product.category_id);
                return (
                  <div
                    key={product.id}
                    className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200 group"
                    data-product-shop="true"
                  >
                    <Link to={`/product/${product.id}`} className="block h-52 bg-background-100 overflow-hidden relative no-underline">
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-shopping-bag-line text-3xl text-foreground-300"></i>
                        </div>
                      )}
                      <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
                        {digital && (
                          <span className="px-2 py-0.5 bg-secondary-500 text-background-50 rounded-full text-xs font-semibold flex items-center gap-1">
                            <i className="ri-download-cloud-2-line"></i>Digital
                          </span>
                        )}
                        {isNewProduct(product.created_at) && (
                          <span className="px-2 py-0.5 bg-primary-500 text-background-50 rounded-full text-xs font-semibold">
                            Nouveau
                          </span>
                        )}
                        {promo && (
                          <span className="px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-xs font-semibold">
                            Promo
                          </span>
                        )}
                      </div>
                      <FavoriteBadge
                        active={isFav('product', product.id)}
                        onToggle={() => toggleFavItem(product.id, product.name, { image: imgSrc || null, price: eff, currency: cur })}
                        className="absolute top-2 right-2 z-10"
                      />
                    </Link>
                    <div className="p-4">
                      <Link to={`/product/${product.id}`} className="no-underline">
                        <h3 className="text-sm font-semibold text-foreground-900 mb-1 line-clamp-2 hover:text-primary-600 transition-colors">{product.name}</h3>
                      </Link>
                      {chain.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap mb-1.5">
                          {chain.map((c, idx) => (
                            <span key={c.id} className="flex items-center gap-1">
                              {idx > 0 && <i className="ri-arrow-right-s-line text-foreground-300 text-[10px]"></i>}
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-50 text-secondary-700 whitespace-nowrap">{c.nom}</span>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        {promo && product.discount_price != null ? (
                          <>
                            <span className="text-base font-bold text-primary-600">
                              {display(Number(product.discount_price), cur)}
                            </span>
                            <span className="text-xs text-foreground-400 line-through">
                              {display(price, cur)}
                            </span>
                          </>
                        ) : (
                          <span className="text-base font-bold text-primary-600">
                            {price ? display(price, cur) : ''}
                          </span>
                        )}
                      </div>
                      {price > 0 && original(eff, cur) && (
                        <span className="block mt-0.5 text-[11px] text-foreground-400">
                          {original(eff, cur)}
                        </span>
                      )}

                      {isTenant && (
                        <button
                          type="button"
                          onClick={() => handleAdd(product)}
                          disabled={outOfStock}
                          className={`mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            outOfStock
                              ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                              : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                          }`}
                        >
                          <i className={digital ? 'ri-download-2-line' : 'ri-shopping-cart-line'}></i>
                          {outOfStock ? 'Rupture de stock' : digital ? 'Ajouter (digital)' : 'Ajouter au panier'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}